import { Badge, Button, DataGrid, Tooltip } from '@ibsys/kit';
import type { DataGridLeafColumn } from '@ibsys/kit';
import { RefreshCwIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RunListItem, RunStatus, RunTaskDetail, fetchRuns, useRestartRun } from '@/entities/jobs';
import { formatTimeDate } from '@/shared/lib/utils/date';
import { formatTimeInterval, withSecondsLabel } from '@/shared/lib/utils/string';
import { useTimeDisplayFormat } from '@/entities/settings';
import { TaskStatusCircles } from './TaskStatusCircles';
import { RESTARTABLE_STATUSES, STATUS_VARIANTS } from './utils';

/** Пропсы таблицы истории запусков пайплайнов */
type RunsTableProps = {
  total: number;
  /** Фильтр по статусу; undefined — все запуски */
  status?: RunStatus;
  /** Смена значения сбрасывает кеш DataGrid и инициирует перезагрузку данных */
  refreshKey: number;
  onTaskClick?: (runId: string, task: RunTaskDetail) => void;
};

const DEFAULT_PAGE_SIZE = 25;

/** Таблица истории запусков пайплайнов с постраничной навигацией и ресайзом колонок */
export const RunsTable: FC<RunsTableProps> = ({ total, status, refreshKey, onTaskClick }) => {
  const { t } = useTranslation(['common', 'runs']);
  const timeDisplayFormat = useTimeDisplayFormat();
  const restartMutation = useRestartRun();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Сброс на первую страницу при смене фильтра статуса
  useEffect(() => {
    setPage(0);
  }, [status]);

  const handleRestart = useCallback(
    (e: React.MouseEvent, runId: string) => {
      e.stopPropagation();
      restartMutation.mutate({ runId });
    },
    [restartMutation],
  );

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  const fetchPage = useCallback(
    async (p: number, ps: number) => {
      const result = await fetchRuns({ status, limit: ps, offset: p * ps });
      return result.items;
    },
    [status],
  );

  const cacheVersion = `${status ?? 'all'}-${refreshKey}`;

  const columns: DataGridLeafColumn<RunListItem>[] = useMemo(
    () => [
      {
        id: 'restart',
        accessor: 'status',
        header: () => <span className="sr-only">{t('runs:restart')}</span>,
        size: 50,
        cell: ({ row }) => {
          const canRestart = RESTARTABLE_STATUSES.includes(row.status);
          if (!canRestart) return null;
          return (
            <Tooltip tooltip={t('runs:restartTooltip')}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('runs:restart')}
                onClick={(e) => handleRestart(e, row.runId)}
              >
                <RefreshCwIcon className="size-4" />
              </Button>
            </Tooltip>
          );
        },
      },
      {
        id: 'description',
        accessor: 'pipeline',
        header: t('runs:table.description'),
        wrap: true,
        cell: ({ row }) => (
          <Tooltip tooltip={row.pipeline.id}>
            <span>{row.pipeline.description || t('runs:noDescription')}</span>
          </Tooltip>
        ),
      },
      {
        id: 'tasks',
        accessor: 'tasksCount',
        header: t('runs:table.tasks'),
        size: 350,
        cell: ({ row }) => (
          <TaskStatusCircles
            runId={row.runId}
            status={row.status}
            tasksCount={row.tasksCount}
            onTaskClick={onTaskClick}
          />
        ),
      },
      {
        id: 'dateTime',
        accessor: 'createdAt',
        header: t('runs:table.dateTime'),
        size: 180,
        cell: ({ row }) => <span>{row.startedAt ? formatTimeDate(row.startedAt) : '-'}</span>,
      },
      {
        id: 'durationSeconds',
        accessor: 'durationSeconds',
        header: withSecondsLabel(t('runs:table.duration'), timeDisplayFormat, t('common:secondsSuffix')),
        size: 110,
        cell: ({ row }) => (
          <span>{row.durationSeconds !== null ? formatTimeInterval(row.durationSeconds, timeDisplayFormat) : '-'}</span>
        ),
      },
      {
        id: 'createdBy',
        accessor: 'createdBy',
        header: t('runs:table.createdBy'),
        size: 160,
        cell: ({ row }) => {
          const user = row.createdBy;
          if (!user) return <span>{t('runs:systemUser')}</span>;
          return (
            <Link
              to={`/users/lists/${user.id}`}
              className="text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {user.displayName}
            </Link>
          );
        },
      },
      {
        id: 'status',
        accessor: 'status',
        header: t('runs:table.status'),
        size: 130,
        cell: ({ row }) => {
          const tooltipText = t('runs:tooltipProgress', {
            status: t(`runs:status.${row.status}`),
            completed: row.progress.completed,
            total: row.progress.total,
            percent: row.progress.percent,
          });
          return (
            <Tooltip tooltip={tooltipText}>
              <Badge variant={STATUS_VARIANTS[row.status]} outline>
                {t(`runs:status.${row.status}`)}
              </Badge>
            </Tooltip>
          );
        },
      },
    ],
    [t, timeDisplayFormat, handleRestart, onTaskClick],
  );

  return (
    <DataGrid
      columns={columns}
      dataSource={{
        mode: 'paginated',
        totalCount: total,
        page,
        pageSize,
        onPageChange: setPage,
        onPageSizeChange: handlePageSizeChange,
        pageSizeOptions: [10, 25, 50],
        fetchPage,
        cacheVersion,
      }}
      getRowId={(row) => row.runId}
      enableColumnResizing
      emptyState={<div className="p-4 text-muted-foreground">{t('runs:noRuns')}</div>}
    />
  );
};
